import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import type { IPartnerDocumentSection } from '../../../lib/documentsAndLinks'
import { selectCockpitPartnerData } from '../../../model/selectors'
import { buildDocumentsTreeGroups } from '../lib/buildDocumentsTreeGroups'
import type { TDocumentType } from '../types'

export const useDocumentsTreeContent = (sections: IPartnerDocumentSection[]) => {
  const [documentType, setDocumentType] = useState<TDocumentType>('elt')
  const [grouping, setGrouping] = useState<'partner' | 'creator'>('partner')
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const partners = useSelector(selectCockpitPartnerData)
  const countries = useMemo(
    () => new Map(partners.map((partner) => [partner.PartnerId.trim(), partner.Country])),
    [partners]
  )
  const groups = useMemo(
    () => buildDocumentsTreeGroups(sections, documentType, grouping, search),
    [sections, documentType, grouping, search]
  )
  const toggle = useCallback((id: string) => {
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])
  const changeType = useCallback((type: TDocumentType) => {
    setDocumentType(type)
    setCollapsed(new Set())
  }, [])
  const changeGrouping = useCallback((next: 'partner' | 'creator') => {
    setGrouping(next)
    setCollapsed(new Set())
  }, [])
  const expandAll = useCallback(() => setCollapsed(new Set()), [])
  const collapseAll = useCallback(() => {
    setCollapsed(new Set(groups.map((group) => group.id)))
  }, [groups])
  return {
    documentType,
    grouping,
    search,
    collapsed,
    countries,
    groups,
    toggle,
    changeType,
    changeGrouping,
    setSearch,
    expandAll,
    collapseAll,
  }
}
